import { useMemo, useState } from 'react'
import KpiCard from '../../dashboard/KpiCard'
// (useMemo já importado acima)
import { BADGE_BASE } from '../../layout/mobile/mobileTokens'
import type { InactiveProduct } from '../../../hooks/useInactiveProducts'
import { suggestEncalheExit, daysSince, ENCALHE_ACTION_BADGE } from '../../../lib/encalheStrategy'

const brl = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
const fmtDate = (iso: string | null) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '—'

const ENCALHE_DAYS = 90

interface Props {
  products: InactiveProduct[]
  isAdmin: boolean
  reactivating: string | null
  deleting: string | null
  onReactivate: (id: string, name: string) => void
  onDelete: (id: string, name: string, sku: string) => void
}

export default function MobileInactiveList({
  products, isAdmin, reactivating, deleting, onReactivate, onDelete,
}: Props) {
  const [search, setSearch] = useState('')
  const [encalheOnly, setEncalheOnly] = useState(false)

  // useState initializer = call-once-at-mount, compatível com regra react-hooks/purity.
  const [encalheCutoffMs] = useState(() => Date.now() - ENCALHE_DAYS * 24 * 60 * 60 * 1000)
  const [nowMs] = useState(() => Date.now())
  const isEncalhe = (p: InactiveProduct) =>
    p.current_stock > 0 && !!p.last_sale_at && new Date(p.last_sale_at).getTime() < encalheCutoffMs

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return products.filter(p => {
      if (q && !p.sku.toLowerCase().includes(q) && !p.name.toLowerCase().includes(q)) return false
      if (encalheOnly && !isEncalhe(p)) return false
      return true
    })
  }, [products, search, encalheOnly, encalheCutoffMs]) // eslint-disable-line react-hooks/exhaustive-deps

  // KPIs
  const stockedInactive = products.filter(p => p.current_stock > 0)
  const stockedValue = stockedInactive.reduce((s, p) => s + p.current_stock * (p.cost ?? 0), 0)
  const encalheItems = products.filter(isEncalhe)
  const encalheValue = encalheItems.reduce((s, p) => s + p.current_stock * (p.cost ?? 0), 0)

  return (
    <div className="space-y-3">
      {/* KPIs em cards verticais */}
      <div className="grid grid-cols-1 gap-2">
        <KpiCard
          label="Valor parado em estoque"
          value={brl(stockedValue)}
          tone="warning"
          hint={`${stockedInactive.length} produto(s) inativo(s) com saldo`}
        />
        <KpiCard
          label="Encalhe (+90 dias sem venda)"
          value={brl(encalheValue)}
          tone="danger"
          hint={encalheOnly ? 'clique para limpar filtro' : `${encalheItems.length} produto(s) · clique para filtrar`}
          onClick={() => setEncalheOnly(v => !v)}
          active={encalheOnly}
        />
      </div>

      {/* Busca + filtro encalhe */}
      <input
        type="search"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Buscar SKU ou produto…"
        className="w-full h-11 bg-paper border border-line rounded-2xl px-3 text-ink text-sm placeholder:text-ink-faint"
      />

      {/* Contador */}
      <p className="text-xs text-ink-faint text-right">
        {filtered.length === products.length ? `${products.length} itens` : `${filtered.length} de ${products.length}`}
      </p>

      {/* Cards de produto inativo */}
      {filtered.length === 0 ? (
        <div className="py-16 text-center text-ink-faint">
          <p className="text-2xl mb-2">📭</p>
          <p>Nenhum produto encontrado.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => {
            const encalhe = isEncalhe(p)
            const value = p.current_stock * (p.cost ?? 0)
            return (
              <div key={p.id} className="bg-surface border border-line rounded-xl overflow-hidden">
                <div className="p-3">
                  {/* Header: nome + badge encalhe */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-ink text-sm font-semibold leading-tight line-clamp-2">{p.name}</p>
                      <p className="text-[11px] text-ink-muted mt-1 font-mono">
                        {p.sku}{p.suppliers?.name ? ` · ${p.suppliers.name}` : ''}
                      </p>
                    </div>
                    {encalhe && (
                      <span className={`${BADGE_BASE} bg-danger/10 text-danger border border-danger/30`}>
                        ⚠ Encalhe
                      </span>
                    )}
                  </div>

                  {/* Métricas em grade */}
                  <div className="grid grid-cols-3 gap-px bg-line rounded-lg overflow-hidden text-center">
                    <div className="bg-sunken py-2">
                      <p className="text-[9px] text-ink-faint uppercase tracking-wider">Estoque</p>
                      <p className={`text-sm font-bold tabular-nums ${p.current_stock > 0 ? 'text-ink' : 'text-ink-faint'}`}>
                        {p.current_stock}
                      </p>
                    </div>
                    <div className="bg-sunken py-2">
                      <p className="text-[9px] text-ink-faint uppercase tracking-wider">Valor</p>
                      <p className="text-sm font-bold tabular-nums text-ink">{brl(value)}</p>
                    </div>
                    <div className="bg-sunken py-2">
                      <p className="text-[9px] text-ink-faint uppercase tracking-wider">Últ. venda</p>
                      <p className={`text-sm font-bold tabular-nums ${encalhe ? 'text-danger' : 'text-ink-muted'}`}>
                        {fmtDate(p.last_sale_at)}
                      </p>
                    </div>
                  </div>

                  {/* Sugestão de saída de encalhe (regra determinística) */}
                  {encalhe && (() => {
                    const s = suggestEncalheExit({
                      currentStock: p.current_stock, cost: p.cost, marginPct: p.margin_pct,
                      abcCurve: p.abc_curve, daysWithoutSale: daysSince(p.last_sale_at, nowMs), isConsigned: p.is_consigned,
                    })
                    return (
                      <div className="mt-2 rounded-lg border border-line bg-sunken/50 p-2.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-bold uppercase tracking-wide text-ink-faint">Sugestão de saída</span>
                          <span className={`${BADGE_BASE} ${ENCALHE_ACTION_BADGE[s.action]}`}>{s.action}</span>
                        </div>
                        <p className="text-[11px] text-ink-muted mt-1 leading-snug">{s.reason}</p>
                        <p className="text-[11px] text-ink-faint mt-1">Libera <b className="text-ink tabular-nums">{brl(s.capital)}</b> de capital parado.</p>
                      </div>
                    )
                  })()}
                </div>

                {/* Ações */}
                <div className="flex border-t border-line divide-x divide-line">
                  <button
                    onClick={() => onReactivate(p.id, p.name)}
                    disabled={reactivating === p.id || deleting === p.id}
                    className="flex-1 py-2.5 text-xs font-semibold text-green-300 active:bg-green-900/30 disabled:opacity-50 touch-action-manip"
                  >
                    {reactivating === p.id ? 'Reativando…' : '↩ Reativar'}
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => onDelete(p.id, p.name, p.sku)}
                      disabled={reactivating === p.id || deleting === p.id}
                      className="flex-1 py-2.5 text-xs font-semibold text-red-300 active:bg-red-900/30 disabled:opacity-50 touch-action-manip"
                    >
                      {deleting === p.id ? 'Excluindo…' : '🗑 Excluir'}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
